/*
 *
 * ApplicationCalendar
 *
 */

import Toolbar from '@mui/material/Toolbar';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import Calendar from '~/components/Calendar';
import CalendarControls from '~/components/CalendarControls';
import { ShiftApplicationEvent } from '~/components/ShiftApplicationEvent';
import { useCalendarDisplayType } from '~/components/WithCalendarDisplayType';
import { ApplicationEventResource } from '~/generated';
import { useProjectCalendarEvents } from '~/hooks/api/useProjectCalendarEvents';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { useShiftApplicationEvents } from '~/hooks/api/useShiftApplicationEvents';
import styles from './styles.module.css';

const route = getRouteApi('/application/$year/$week/$day/{-$shiftId}');

const ApplicationCalendar = () => {
  const calendarDisplayType = useCalendarDisplayType();
  const navigate = useNavigate();
  const params = route.useParams();

  const { isLoading: isLoadingCalendarEvents, data: calendarEventsData } =
    useProjectCalendarEvents(
      parseInt(params.year, 10),
      parseInt(params.week, 10)
    );
  const { isLoading: isLoadingSettings, data: planningSettingsData } =
    useSettingsByCategory();
  const { isLoading: isLoadingPublisher, data: publisherData } =
    usePublisherMe();
  const { isLoading: isLoadingApplicationEvents, data: applicationEventsData } =
    useShiftApplicationEvents(
      parseInt(params.year, 10),
      parseInt(params.week, 10)
    );

  const displayedWeek = moment()
    .isoWeekYear(parseInt(params.year, 10))
    .isoWeek(parseInt(params.week, 10))
    .weekday(parseInt(params.day, 10));

  const navigateToDate = (date: Date) => {
    const momentDate = moment(date);
    navigate({
      to: '/application/$year/$week/$day/{-$shiftId}',
      params: {
        year: momentDate.isoWeekYear().toString(),
        week: momentDate.isoWeek().toString(),
        day: momentDate.weekday().toString(),
      },
    });
  };

  const mapShiftsToEvents = () =>
    applicationEventsData
      ? (applicationEventsData
          .map((applicationEvent) =>
            applicationEvent.shift &&
            applicationEvent.shift.id &&
            applicationEvent.shift.startDate &&
            applicationEvent.shift.endDate
              ? {
                  id: applicationEvent.shift.id,
                  title: '',
                  start: applicationEvent.shift.startDate,
                  end: applicationEvent.shift.endDate,
                }
              : null
          )
          .filter(Boolean) as Array<{
          id: number;
          title: string;
          start: string;
          end: string;
        }>)
      : [];

  const getApplicationEvent = (calEvent: { id: number }) =>
    applicationEventsData?.find(
      (applicationEvent) =>
        applicationEvent.shift && applicationEvent.shift.id === calEvent.id
    );

  const handleClickShift = (applicationEvent: ApplicationEventResource) => {
    const date = moment(applicationEvent.shift?.startDate);
    navigate({
      to: '/application/$year/$week/$day/{-$shiftId}',
      params: {
        year: date.isoWeekYear().toString(),
        week: date.isoWeek().toString(),
        day: date.weekday().toString(),
        shiftId: applicationEvent.shift?.id?.toString() ?? '',
      },
    });
  };

  const renderCalendarEvent = (calEvent: {
    id: number;
    start: string;
    end: string;
  }) => {
    const applicationEvent = getApplicationEvent(calEvent);
    if (!applicationEvent || !applicationEvent.shift?.id || !publisherData)
      return null;

    return (
      <ShiftApplicationEvent
        applicationEvent={applicationEvent}
        isHighlighted={
          params.shiftId ? calEvent.id === parseInt(params.shiftId, 10) : false
        }
        isDimmed={
          params.shiftId ? calEvent.id !== parseInt(params.shiftId, 10) : false
        }
        onSelect={() => handleClickShift(applicationEvent)}
        publisher={publisherData}
        isVehicleWorkflowActive={
          planningSettingsData?.isVehicleWorkflowActive === true || false
        }
      />
    );
  };

  const isFetching =
    isLoadingApplicationEvents ||
    isLoadingSettings ||
    isLoadingPublisher ||
    isLoadingCalendarEvents;

  return (
    <div className={styles['application-calendar']}>
      <Toolbar className={styles.toolbar}>
        <CalendarControls
          date={displayedWeek}
          displayType={calendarDisplayType}
          onChange={navigateToDate}
        />
      </Toolbar>
      <div className={styles.calendar}>
        {!isFetching ? (
          <Calendar
            date={displayedWeek}
            displayType={calendarDisplayType}
            events={mapShiftsToEvents()}
            onEventRender={renderCalendarEvent}
            projectCalendarEvents={calendarEventsData?.events}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ApplicationCalendar;
